import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from "react-redux";
import { useParams } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import { Button, FormHelperText, FormLabel, Tooltip} from '@material-ui/core';

import { TextInput } from './TextInput';

import api from '../../utils/api';
import makePath from '../../utils/makePath';
import generateId from '../../utils/generateId';

import { addAsset, deleteItem, deleteAsset, setItem } from "../../actions";
import UuidContext from '../../contexts/UuidContext';
import { useDirty, useFormElementCleanUp, usePrevious, useValidation } from '../../hooks';
import { LoadingButton } from '../CustomMui';
import { FormElementWrapper } from './FormElementWrapper';
import LockContext from '../../contexts/LockContext';

const root = process.env.REACT_APP_API_ROOT || '';

const useStyles = makeStyles((theme) => ({
  fieldset: {
    paddingTop: 0,
    paddingBottom: 0,
    marginRight: 0,
    marginLeft: 0,
    minInlineSize: 'auto',
    border: 'none',
    display: 'inline-block', // default of inline-flex doesn't work for fieldset
    // margin: '0 10px', // switch margin properties for padding properties to match other inline-flex FormControl where margins don't collapse
    // padding: '12px 0 20px 0' 
  },
  legend: {
    fontWeight: '600',
    marginBottom: '10px',
  },
  icon: {
    color: theme.palette.primary.main,
    fontSize: 12,
    margin: '2px 0 10px 5px',
    '&:hover': {
      fontWeight: 700,
      color: theme.palette.primary.hover
    }
  },
  input: {
    display: 'none',
  },
  uploadButtonLabel: {
    width: 'auto',
  },
  button: {
    width: 175
  },
  filenameWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  filename: {
    overflow: 'auto',
    backgroundColor: theme.palette.neutral.light3,
    borderRadius: '4px',
    padding: '10.5px 14px',
    marginRight: '7px',
    fontFamily: theme.typography.fontFamilyInputs,
    fontWeight: 500,
  },
  deleteButton: {
    fontSize: '16px'
  },
  alt: {
    margin: '20px 0 0 0'
  }
}));

export const ImageUpload = React.memo(({callback,includeAlt,includeCaption,given = {},help,label,name,optional,tooltip, ...props}) => {

  const [id] = useState(generateId('imageUpload-'));
  const { id:intvId } = useParams();
  const setDirty = useDirty();
  const classes = useStyles();
  
  const { uuid } = useContext(UuidContext);
  const { lock } = useContext(LockContext);

  const isLocked = !!lock.owner && !lock.isCurrent;

  const uploadRef = useRef();

  const path = makePath(props.path,name)
  useFormElementCleanUp(path);

  // redux
  const dispatch = useDispatch();
  const assets = useSelector(state => state.assets);

  // state
  /**
   * @todo
   * Refactor useState to useReducer
   */ 
  const [alt, setAlt] = useState('');
  const [caption, setCaption] = useState('');
  const [credit, setCredit] = useState('');
  const [filename, setFilename] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [loading, setLoading] = useState(false);
  const prevFilename = usePrevious(filename)

  // Validation conditions
  const validate = () => {
    if (!filename && !optional) {
      setError('Missing! This field is required.');
    } else {
      setError('');
    }
  }

  const [error, setError] = useValidation(validate,filename,path,id);

  const updateAlt = update => { 
    setAlt(update.alt); 
  }

  const updateCaption = update => { 
    setCaption(update.caption); 
  }

  const updateCredit = update => { 
    setCredit(update.credit); 
  }


  // Set image values in store when values change
  useEffect(() => {
    const image = { src:filename };

    // If there had been a filename, but there isn't anymore,
    // delete it from the store
    if (prevFilename && filename === '') { 
      dispatch(deleteItem(path))
      return;
    }

    if (alt) { image.alt = alt }
    if (credit) { image.credit = credit }
    if (caption) { image.caption = caption }

    dispatch(setItem(path, image));

    if (callback) {
      callback(name, image);
    }
  }, [alt,callback,caption,credit,dispatch,filename,name,path,prevFilename])

  // Handles uploading the image
  const onInputChange = (e) => {
    setLoading(true)
    
    var reader = new FileReader();

    // Assuming only image
    var file = uploadRef.current.files[0];

    if (!file) {
      setFilename('')
      setAlt('')
      setCaption('')
      setCredit('')
      return;
    }

    // var url = reader.readAsDataURL(file);
    reader.readAsDataURL(file);

    reader.onloadend = (e) => {
      // transform to lower case, replace spaces with hyphens, and strip any other special chars
      const normalizedFilename = file.name.toLowerCase().replaceAll(' ', '-').replace(/[^-a-z0-9_.]/g, '')
      
      const lastDot = normalizedFilename.lastIndexOf('.');
    
      const fName = normalizedFilename.substring(0, lastDot);
      const ext = normalizedFilename.substring(lastDot + 1);
      
      let count = 1;
      let newFileName = normalizedFilename
      
      while (assets[newFileName]) {
        newFileName = `${fName}__${count}.${ext}`;
        count++;
      }

      const formData = new FormData()
      formData.append('File',file,newFileName);
      formData.append('InteractiveUuid',uuid);

      const success = (lockId) => {
        
        if (lockId) {
          formData.append('LockUuid',lockId)
        }

        api.uploadImage(formData)
          .then(res => {
            setFilename(newFileName);
            dispatch(addAsset({
              filename: newFileName,
              data: reader.result,
            }))
            setError('')
          })
          .catch(err => {
            setError('An error occurred. Your image was not uploaded.')

            if (err.response) {
              console.log(err.response)
            } else {
              console.log(err)
            }
          
          })
          .finally(() => {
            setLoading(false)
          })
      }

      if (!lock.id && intvId) {
        setDirty(success, () => { 
          setLoading(false);
          setError('This item is locked for edits. Your image was not uploaded.')
        })
      } else {
        success(lock.id);
      }
      
    }
  }

  // Delete image
  const onClearImageClick = () => {
    setLoading(true)

    const success = (lockId) => {

      api.deleteImage(uuid,filename,lockId)
        .then(res => {
          uploadRef.current.value = null;
          dispatch(deleteAsset({ filename }))

          // Empty these values so that the store and state remain in sync
          setAlt('');
          setFilename('')
          setCaption('');
          setCredit('');
          setError('')
        })
        .catch(err => {
          setUploadError('An error occurred. Your image was not deleted.')

          if (err.response) {
            console.log(err.response)
          } else {
            console.log(err)
          }
        
        })
        .finally( () => {
          setLoading(false)
        })
    }
      

    if (!lock.id && intvId) {
      setDirty(success, () => {
        setLoading(false);
        setError('This item is locked for edits. Your image was not deleted.')
      })
    } else {
      success(lock.id);
    }

  }
  
  // Handles given image
  useEffect(() => {
    if (given) {
      const { src, alt, credit, caption } = given;
      if (src) { 
        setFilename(src);
        dispatch(addAsset({
          filename: src,
          data: `${root}/preview/${intvId}/${src}`,
        }));
      } else {
        return;
      }

      if (alt) setAlt(alt);
      if (credit) setCredit(credit);
      if (caption) setCaption(caption);

    }
  }, [dispatch,given,intvId])

  return (      

    <FormElementWrapper component="fieldset" className={classes.fieldset}>
      
      <div style={{display: 'flex'}}>

        <FormLabel className={classes.legend} component="legend">{label}</FormLabel>
        
        {tooltip ? 
          <Tooltip title={tooltip} arrow interactive>
            <i className={`far fa-question-circle ${classes.icon}`}></i>
          </Tooltip>
        : '' }

      </div>
    
      <input
        ref={uploadRef}
        accept="image/jpeg, image/png"
        className={classes.input}
        id={id}
        type="file"
        onChange={onInputChange}
        aria-describedby={error ? `${id}-error ${id}-help` : ''}
        disabled={isLocked}
      />

      {!filename 
        ? <label htmlFor={id} className={classes.uploadButtonLabel}>
            <LoadingButton
              buttonProps={{
                color:"primary",
                component:"span",
                className:classes.button,
                disabled:isLocked
              }}
              loading={loading}
            >
              Choose File&nbsp;&nbsp;<i className="fas fa-image"></i>
            </LoadingButton>
          </label>
        : <div className={classes.filenameWrapper}>
            
            {/* filename class allows it to change bg color when parent accordion is using grayTheme */}
            <div className={`${classes.filename} filename`}>{filename}</div>

            <Button 
              variant="text" 
              color="primary" 
              onClick={onClearImageClick} 
              aria-label="Delete" 
              className={classes.deleteButton}
              disabled={isLocked}
            >
              <i className="far fa-trash-alt"></i>
            </Button>
          </div>
      }

      {error || uploadError
        ? <FormHelperText error id={`${id}-error`}>{error ? error : uploadError}</FormHelperText>
        : ''
      }

      {help ? 
        <FormHelperText id={`${id}-help`}>{help}</FormHelperText>
      : ''}

      {includeAlt && filename ? 

        <TextInput
          noMargin
          className={classes.alt}
          multiline
          rows={5}
          name="alt"
          updateParent={updateAlt}
          label={props.altLabel} 
          help={help}
          placeholder={props.altPlaceholder}
          InputLabelProps={ props.altPlaceholder ? { shrink: true } : {} }
          given={given.alt}
        />
        
      : '' }

      {includeCaption && filename ? 
        <>
          <TextInput
            noMargin
            className={classes.alt}
            multiline
            rows={5}
            InputLabelProps={ props.captionPlaceholder ? { shrink: true } : {} }
            label={props.captionLabel} 
            name="caption" 
            updateParent={updateCaption}
            placeholder={props.captionPlaceholder}
          />
          <TextInput
            noMargin
            className={classes.alt}
            name="credit"
            label={props.creditLabel}
            updateParent={updateCredit}
          />
        </>
      : '' }

    </FormElementWrapper>
  )

})

ImageUpload.propTypes = {
  callback: PropTypes.func,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  classes: PropTypes.string,
  includeAlt: PropTypes.bool,
  help: PropTypes.string
}